// Downscale an oversized raster image in the browser so a normal phone
// photo of an artwork can be uploaded without the customer having to go
// away and shrink it themselves.
//
// RK24356 (2026-07-29): a customer photographed her reference illustration,
// got a ~16MB JPEG, and every upload attempt was refused with "make it
// smaller". She gave up and chose "I'll send it later", which put her order
// on hold. Meanwhile an iPhone HEIC of the same artwork would have been
// re-encoded and downscaled automatically by the upload portal. The harder
// format worked and the easy one didn't.
//
// Digitising never uses more than roughly 2400px on the long edge, so a
// downscale here costs nothing in stitch quality.

/** Long-edge sizes to try, largest first. */
const EDGES = [2400, 1800, 1400, 1000];
const QUALITY = 0.9;

export function isShrinkableImage(file: File): boolean {
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  return (
    /^image\/(jpeg|png|webp)$/.test(type) || /\.(jpe?g|png|webp)$/.test(name)
  );
}

/**
 * Re-encodes `file` as a JPEG small enough to fit `maxBytes`, stepping the
 * long edge down until it does. Resolves with the original file when it is
 * already under the cap. Rejects when the image cannot be decoded or will
 * not fit even at the smallest step (e.g. a PDF, which cannot be re-encoded
 * this way).
 */
export async function shrinkImage(file: File, maxBytes: number): Promise<File> {
  if (file.size <= maxBytes) return file;
  if (!isShrinkableImage(file)) throw new Error("not_shrinkable");

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("decode_failed"));
      el.src = url;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error("decode_failed");

    for (const maxEdge of EDGES) {
      const scale = Math.min(1, maxEdge / Math.max(w, h));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no_canvas");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/jpeg", QUALITY),
      );
      if (blob && blob.size <= maxBytes) {
        const base = (file.name || "artwork").replace(/\.[^.]+$/, "");
        return new File([blob], `${base}.jpg`, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
      }
    }
    throw new Error("still_too_large");
  } finally {
    URL.revokeObjectURL(url);
  }
}
